import { VidExtraVideoPublishStatus as PublishStatus } from "@apis/generated/openapi";
import type { UserAssetV2Record } from "@apis/http/api";
import { DarkTooltip } from "@components/DarkTooltip";
import { useFormatDateTime } from "@hooks/useFormatDateTime";
import { IconPublishPosted, IconPublishScheduled } from "@icons/index";
import { Trans, useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

export interface LeftTopCornerProps {
  record: UserAssetV2Record;
}

const publishStatusConfig: Partial<
  Record<PublishStatus, { bgClass: string; label: "Published" | "Scheduled"; Icon: typeof IconPublishPosted }>
> = {
  [PublishStatus.SUCCESS]: {
    bgClass: "bg-kds-success",
    label: "Published",
    Icon: IconPublishPosted,
  },
  [PublishStatus.PENDING_PUBLISH]: {
    bgClass: "bg-kds-accent",
    label: "Scheduled",
    Icon: IconPublishScheduled,
  },
};

export const LeftTopCorner = (props: LeftTopCornerProps) => {
  const { record } = props;
  const { videoPublishStatus, videoPublishTime } = record.vidExtra ?? {};
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  if (!videoPublishStatus) return null;

  const config = publishStatusConfig[videoPublishStatus];

  if (!config) return null;
  const dateTime = formatDateTime(videoPublishTime, "YYYY-MM-DD HH:mm");
  const { bgClass, label, Icon } = config;
  const isPublished = videoPublishStatus === PublishStatus.SUCCESS;
  return (
    <DarkTooltip
      title={
        <div onClick={(e) => e.stopPropagation()}>
          {dateTime ? `${t(isPublished ? "Published:" : "Est. Publish:")} ${dateTime}` : ""}
        </div>
      }
    >
      <div
        className={twMerge(
          `absolute top-[12px] left-[12px] z-10 inline-flex h-[24px] items-center justify-center gap-[2px] rounded-full px-[8px] py-[4px]`,
          bgClass,
        )}
      >
        <Icon className="size-[16px]" />
        <span className="whitespace-nowrap text-[14px] font-medium leading-[16px] text-kds-light">
          <Trans>{label}</Trans>
        </span>
      </div>
    </DarkTooltip>
  );
};
